import { useContext } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import ListItem from 'components/list/list-item/ListItem';
import ListItemStatus from 'components/list/list-item/ListItemStatus';
import ListItemLabelWrapper from 'components/list/list-item/ListItemLabelWrapper';
import ListItemLabel from 'components/list/list-item/ListItemLabel';
import messages from 'messages';
import {
  ROUTE_SETTINGS_USERS,
  ROUTE_SETTINGS_ORGANISATIONS,
  ROUTE_SETTINGS_GLOBAL,
  ROUTE_SETTINGS_BENCHMARKS,
  ROUTE_SETTINGS_TESTSETS,
  ROUTE_SETTINGS_USAGE,
  ROUTE_SETTINGS_DEMODATA,
  ROUTE_SETTINGS_PROFILE,
  ROUTE_SETTINGS_PROFILE_MFA,
  ROUTE_SETTINGS_EXPORT_DATA,
  ROUTE_SETTINGS_SEARCH_USERS
} from 'routes/RouteList';
import List from 'components/list/List';
import ListBody from 'components/list/ListBody';
import {
  PanelTitleWrapper,
  PanelTitle,
  PanelBody,
  Panel
} from 'components/panel';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { StoreContext } from 'index';

const OverView = () => {
  const {
    authStore: {
      user,
      user: { config, rootEntityId }
    }
  } = useContext(StoreContext);
  const { push } = useHistory();
  const { setting } = useParams();
  const ability = useAbility(AbilityContext);

  return (
    <Panel style={{ overflowX: 'hidden', zIndex: 2 }}>
      <PanelBody refactor>
        <PanelTitleWrapper>
          <PanelTitle>
            <FormattedMessage {...messages.settingsTitle} />
          </PanelTitle>
        </PanelTitleWrapper>
        <List>
          <ListBody>
            <ListItem
              active={setting === 'global'}
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_GLOBAL, {
                    entityId: rootEntityId
                  })
                )
              }
            >
              <ListItemStatus
                mediumIcon
                statusIcon="setup-preferences"
                strokeColor="color-neutral-x-light"
              />
              <ListItemLabelWrapper>
                <ListItemLabel label>
                  <FormattedMessage {...messages.settingsGlobalListItem} />
                </ListItemLabel>
              </ListItemLabelWrapper>
            </ListItem>
            <ListItem
              active={setting === 'users'}
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_USERS, { entityId: rootEntityId })
                )
              }
            >
              <ListItemStatus
                mediumIcon
                statusIcon="users-settings"
                strokeColor="color-neutral-x-light"
              />
              <ListItemLabelWrapper>
                <ListItemLabel label>
                  <FormattedMessage {...messages.settingsUserListItem} />
                </ListItemLabel>
              </ListItemLabelWrapper>
            </ListItem>
            {ability.can('manage', 'Organisations') && (
              <ListItem
                active={setting === 'organisations'}
                onClick={() =>
                  push(
                    generatePath(ROUTE_SETTINGS_ORGANISATIONS, {
                      entityId: rootEntityId
                    })
                  )
                }
              >
                <ListItemStatus
                  mediumIcon
                  statusIcon="school-settings"
                  strokeColor="color-neutral-x-light"
                />
                <ListItemLabelWrapper>
                  <ListItemLabel label>
                    <FormattedMessage
                      {...messages.settingsOrganisationsListItem}
                    />
                  </ListItemLabel>
                </ListItemLabelWrapper>
              </ListItem>
            )}
          </ListBody>
        </List>

        {(ability.can('manage', 'Benchmarks') ||
          ability.can('manage', 'Testsets')) &&
          (config.benchmarksEnabled || config.testSetsEnabled) && (
            <List>
              <ListBody>
                {ability.can('manage', 'Benchmarks') &&
                  config.benchmarksEnabled && (
                    <ListItem
                      active={setting === 'benchmarks'}
                      onClick={() =>
                        push(
                          generatePath(ROUTE_SETTINGS_BENCHMARKS, {
                            entityId: rootEntityId
                          })
                        )
                      }
                    >
                      <ListItemStatus statusIcon="benchmarks" />
                      <ListItemLabelWrapper>
                        <ListItemLabel label>
                          <FormattedMessage
                            {...messages.settingsBenchmarksListItem}
                          />
                        </ListItemLabel>
                      </ListItemLabelWrapper>
                    </ListItem>
                  )}
                {ability.can('manage', 'Testsets') &&
                  config.testSetsEnabled && (
                    <ListItem
                      active={setting === 'testsets'}
                      onClick={() =>
                        push(
                          generatePath(ROUTE_SETTINGS_TESTSETS, {
                            entityId: rootEntityId
                          })
                        )
                      }
                    >
                      <ListItemStatus
                        mediumIcon
                        statusIcon="archive-drawer"
                        strokeColor="color-neutral-x-light"
                      />
                      <ListItemLabelWrapper>
                        <ListItemLabel label>
                          <FormattedMessage
                            {...messages.settingsTestSetsListItem}
                          />
                        </ListItemLabel>
                      </ListItemLabelWrapper>
                    </ListItem>
                  )}
              </ListBody>
            </List>
          )}
        {ability.can('manage', 'Organisation') && (
          <>
            <PanelTitleWrapper>
              <PanelTitle>
                <FormattedMessage
                  {...messages.settingsOrganisationsListTitle}
                />
              </PanelTitle>
            </PanelTitleWrapper>

            <List>
              <ListBody>
                <ListItem
                  active={setting === 'usage'}
                  onClick={() =>
                    push(
                      generatePath(ROUTE_SETTINGS_USAGE, {
                        entityId: rootEntityId
                      })
                    )
                  }
                >
                  <ListItemStatus
                    statusIcon="usage"
                    strokeColor="color-neutral-x-light"
                    extraClassNamesIcon="u-padding-small"
                  />
                  <ListItemLabelWrapper>
                    <ListItemLabel label>
                      <FormattedMessage {...messages.accountUsageLabel} />
                    </ListItemLabel>
                  </ListItemLabelWrapper>
                </ListItem>
                <ListItem
                  active={setting === 'export'}
                  onClick={() =>
                    push(
                      generatePath(ROUTE_SETTINGS_EXPORT_DATA, {
                        entityId: rootEntityId
                      })
                    )
                  }
                >
                  <ListItemStatus
                    statusIcon="download-white"
                    strokeColor="color-neutral-x-light"
                    extraClassNamesIcon="u-padding-small"
                  />
                  <ListItemLabelWrapper>
                    <ListItemLabel label>
                      <FormattedMessage {...messages.exportDataLabel} />
                    </ListItemLabel>
                  </ListItemLabelWrapper>
                </ListItem>
                {user.superadmin && (
                  <ListItem
                    active={setting === 'demodata'}
                    onClick={() =>
                      push(
                        generatePath(ROUTE_SETTINGS_DEMODATA, {
                          entityId: rootEntityId
                        })
                      )
                    }
                  >
                    <ListItemStatus
                      statusIcon="data-demo"
                      strokeColor="color-neutral-x-light"
                      extraClassNamesIcon="u-padding-small"
                    />
                    <ListItemLabelWrapper>
                      <ListItemLabel label>
                        <FormattedMessage {...messages.accountDemoDataLabel} />
                      </ListItemLabel>
                    </ListItemLabelWrapper>
                  </ListItem>
                )}
                {user.superadmin && (
                  <ListItem
                    active={setting === 'search-users'}
                    onClick={() =>
                      push(
                        generatePath(ROUTE_SETTINGS_SEARCH_USERS, {
                          entityId: rootEntityId
                        })
                      )
                    }
                  >
                    <ListItemStatus
                      mediumIcon
                      statusIcon="users-settings"
                      strokeColor="color-neutral-x-light"
                    />
                    <ListItemLabelWrapper>
                      <ListItemLabel label>
                        <FormattedMessage
                          {...messages.settingsSearchUsersLabel}
                        />
                      </ListItemLabel>
                    </ListItemLabelWrapper>
                  </ListItem>
                )}
              </ListBody>
            </List>
          </>
        )}

        <PanelTitleWrapper>
          <PanelTitle>
            <FormattedMessage {...messages.settingsProfileTitle} />
          </PanelTitle>
        </PanelTitleWrapper>

        <List>
          <ListBody>
            <ListItem
              active={
                setting === 'profile'
                //   ROUTE_SETTINGS_PROFILE === match.path ||
                //   ROUTE_SETTINGS_PROFILE_EDIT === match.path
              }
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_PROFILE, {
                    entityId: rootEntityId
                  })
                )
              }
            >
              <ListItemStatus
                statusIcon="profile-card"
                strokeColor="color-neutral-x-light"
                extraClassNamesIcon="u-padding-small"
              />
              <ListItemLabelWrapper>
                <ListItemLabel label>
                  <FormattedMessage {...messages.settingsProfilePersonal} />
                </ListItemLabel>
              </ListItemLabelWrapper>
            </ListItem>
            <ListItem
              active={
                setting === 'mfa'
                // ROUTE_SETTINGS_PROFILE === match.path && !match.isExact
              }
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_PROFILE_MFA, {
                    entityId: rootEntityId
                  })
                )
              }
            >
              <ListItemStatus
                statusIcon="unlock"
                strokeColor="color-neutral-x-light"
                extraClassNamesIcon="u-padding-small"
              />
              <ListItemLabelWrapper>
                <ListItemLabel label>
                  <FormattedMessage {...messages.settingsProfileMfa} />
                </ListItemLabel>
              </ListItemLabelWrapper>
            </ListItem>
          </ListBody>
        </List>
      </PanelBody>
    </Panel>
  );
};

export default OverView;
